import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtTRY, fmtQty4 } from "../../lib/format";
import { errText } from "../../lib/errors";
import SmartLookup from "../../components/admin/SmartLookup";
import { Barcode, Plus, Minus, Trash2, Banknote, CreditCard, CheckCircle2, RotateCcw, Scale } from "lucide-react";

const tl = fmtTRY;
const isScaleCode = (c) => /^(27|28)\d{11}$/.test(c);

export default function AdminPOS({ embedded = false }) {
  const [cart, setCart] = useState([]);
  const [code, setCode] = useState("");
  const [payment, setPayment] = useState("nakit");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [daySum, setDaySum] = useState(null);
  const [sumBusy, setSumBusy] = useState(false);
  const scanRef = useRef(null);

  const openDaySummary = async (date) => {
    setSumBusy(true);
    try {
      const { data } = await api.get("/admin/okc/day-summary", { params: date ? { date } : {} });
      setDaySum(data);
    } catch { toast.error("Gün özeti yüklenemedi"); }
    finally { setSumBusy(false); }
  };

  useEffect(() => {
    api.get("/admin/bank-accounts").then(({ data }) => {
      const list = (data || []).filter((a) => ["kasa", "pos", "banka"].includes(a.account_type));
      setAccounts(list);
      const kasa = list.find((a) => a.account_type === "kasa");
      if (kasa) setAccountId(kasa.account_id);
    }).catch(() => {});
    scanRef.current?.focus();
  }, []);

  useEffect(() => {
    const kasa = accounts.find((a) => a.account_type === (payment === "nakit" ? "kasa" : "pos"));
    if (kasa) setAccountId(kasa.account_id);
  }, [payment]); // eslint-disable-line react-hooks/exhaustive-deps

  const addProduct = useCallback((p, qty = 1, note = "") => {
    setCart((c) => {
      const i = c.findIndex((x) => x.product_id === p.product_id && !x.weighed);
      if (i >= 0 && !note) {
        const n = [...c];
        n[i] = { ...n[i], quantity: Math.round((n[i].quantity + qty) * 1000) / 1000 };
        return n;
      }
      return [...c, { product_id: p.product_id, name: p.name, price: p.discount_price || p.price, quantity: qty, weighed: !!note, note }];
    });
    setCode("");
    scanRef.current?.focus();
  }, []);

  const scan = async (raw) => {
    const c = (raw || code).trim();
    if (!c) return;
    try {
      if (isScaleCode(c)) {
        const { data } = await api.get(`/admin/scale-barcode/${c}`);
        addProduct(data.product, data.quantity, `Terazi: ${(data.weight_g / 1000).toFixed(3)} kg (PLU ${data.plu})`);
        toast.success(`${data.product.name} — ${(data.weight_g / 1000).toFixed(3)} kg eklendi`);
      } else {
        const { data } = await api.get(`/admin/stock/lookup/${encodeURIComponent(c)}`);
        addProduct({ product_id: data.product_id, name: data.name, price: data.discount_price || data.price }, 1);
        toast.success(`${data.name} eklendi`);
      }
    } catch (e) { toast.error(errText(e) || "Barkod okunamadı"); setCode(""); }
  };

  const setQty = (i, d) => setCart((c) => c.map((x, j) => j === i ? { ...x, quantity: Math.max(0.001, Math.round((x.quantity + d) * 1000) / 1000) } : x));
  const removeItem = (i) => setCart((c) => c.filter((_, j) => j !== i));

  const totals = useMemo(() => {
    const sub = cart.reduce((s, x) => s + x.price * x.quantity, 0);
    return { count: cart.reduce((s, x) => s + (x.weighed ? 1 : x.quantity), 0), total: Math.round(sub * 100) / 100 };
  }, [cart]);

  const complete = async () => {
    if (!cart.length) { toast.error("Sepet boş"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/okc/sale", {
        items: cart.map((x) => ({ product_id: x.product_id, quantity: x.quantity, unit_price: x.price })),
        payment, account_id: accountId,
      });
      setDone({ ...data, count: totals.count });
      setCart([]);
      toast.success(`Satış tamamlandı — ${data.invoice_number}`);
    } catch (e) { toast.error(errText(e) || "Satış kaydedilemedi"); }
    finally { setBusy(false); scanRef.current?.focus(); }
  };

  if (done) {
    return (
      <div data-testid="pos-success" className="max-w-md mx-auto mt-16 bg-white rounded-2xl border border-stone-200/80 shadow-sm p-8 text-center space-y-4">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
        <h2 className="font-serif text-2xl text-forest">Satış Tamamlandı</h2>
        <p className="text-stone-500 text-sm">{done.invoice_number} · {done.count} kalem</p>
        <p data-testid="pos-success-total" className="text-3xl font-bold text-stone-900">{tl(done.total)} ₺</p>
        <p className="text-xs text-stone-400">Fiş ÖKC cihazından (PAYGO SP630PRO) kesilir. Fatura "Dükkan/ÖKC satışı" olarak işlendi, stok ve kasa otomatik güncellendi.</p>
        <button data-testid="pos-new-sale" onClick={() => { setDone(null); scanRef.current?.focus(); }} className="w-full flex items-center justify-center gap-2 bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors"><RotateCcw className="w-4 h-4" /> Yeni Satış</button>
      </div>
    );
  }

  return (
    <div data-testid="pos-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {!embedded && (
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Hızlı Satış (POS)</h1>
          <p className="text-stone-500 text-sm mt-1">Barkod okutun veya ürün arayın — terazi fişi barkodları (27/28...) otomatik kilo hesaplar.</p>
        </div>
        )}
        <div className={`text-right flex items-end gap-3 ${embedded ? "ml-auto" : ""}`}>
          <button data-testid="pos-day-summary" onClick={() => openDaySummary()} className="text-xs font-bold border border-stone-300 text-stone-700 hover:border-forest px-3.5 py-2.5 rounded-lg transition-colors">Gün Sonu</button>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Toplam</p>
            <p data-testid="pos-total" className="text-2xl font-bold text-forest">{tl(totals.total)} ₺</p>
          </div>
        </div>
      </div>

      {daySum && (
        <div data-testid="pos-daysum-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDaySum(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Gün Sonu Özeti — {daySum.date.split("-").reverse().join(".")}</h3>
              <button data-testid="pos-daysum-close" onClick={() => setDaySum(null)} className="p-1.5 hover:bg-stone-100 rounded-lg text-stone-500">✕</button>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <input data-testid="pos-daysum-date" type="date" className="h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" value={daySum.date} onChange={(e) => e.target.value && openDaySummary(e.target.value)} />
              {sumBusy && <span className="text-xs text-stone-400">Yükleniyor...</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cream-dark/50 rounded-xl p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Satış Adedi</p><p data-testid="pos-daysum-count" className="text-2xl font-bold text-stone-900 mt-1">{daySum.sale_count}</p><p className="text-[11px] text-stone-500">{tl(daySum.item_count)} kalem</p></div>
              <div className="bg-forest/5 border border-forest/20 rounded-xl p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Genel Toplam</p><p data-testid="pos-daysum-total" className="text-2xl font-bold text-forest mt-1">{tl(daySum.grand_total)} ₺</p></div>
              <div className="bg-emerald-50 rounded-xl p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Nakit</p><p className="text-xl font-bold text-emerald-800 mt-1">{tl(daySum.nakit_total)} ₺</p><p className="text-[11px] text-emerald-700">{daySum.nakit_count} satış</p></div>
              <div className="bg-sky-50 rounded-xl p-4"><p className="text-[11px] font-bold uppercase tracking-wider text-sky-700">Kart (POS)</p><p className="text-xl font-bold text-sky-800 mt-1">{tl(daySum.banka_total)} ₺</p><p className="text-[11px] text-sky-700">{daySum.banka_count} satış</p></div>
            </div>
            {daySum.sales.length > 0 && (
              <div className="mt-4 border border-stone-200 rounded-xl divide-y divide-stone-100 max-h-56 overflow-y-auto">
                {daySum.sales.map((s, i) => (
                  <div key={i} data-testid={`pos-daysum-sale-${i}`} className="px-4 py-2 flex items-center justify-between text-sm">
                    <span className="font-mono text-xs text-stone-500">{s.time} · {s.invoice_number}</span>
                    <span className="flex items-center gap-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.payment === "nakit" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>{s.payment === "nakit" ? "NAKİT" : "KART"}</span><b>{tl(s.total)} ₺</b></span>
                  </div>
                ))}
              </div>
            )}
            {daySum.sales.length === 0 && <p className="text-sm text-stone-400 text-center py-6">Bu tarihte ÖKC satışı yok.</p>}
            <p className="text-[11px] text-stone-400 mt-3">ÖKC (PAYGO SP630PRO) Z raporu ile karşılaştırın: nakit + kart toplamı eşleşmelidir.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Barcode className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input ref={scanRef} data-testid="pos-barcode-input" className="w-full h-12 pl-10 pr-4 rounded-xl border border-stone-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-forest/30"
              placeholder="Barkod okutun (ürün veya terazi fişi 27/28...) ve Enter'a basın"
              value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(); } }} />
          </div>
          <button data-testid="pos-scan-add" onClick={() => scan()} className="bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 rounded-xl transition-colors">Ekle</button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Scale className="w-3.5 h-3.5 text-stone-400 shrink-0" />
          <SmartLookup kind="product" testid="pos-product-smart" className="flex-1 [&_input]:!h-10 [&_input]:!w-full [&_input]:!text-sm [&_input]:!bg-white" placeholder="veya ürün adı yazın..."
            onSelect={(p) => addProduct({ product_id: p.product_id, name: p.name, price: p.discount_price || p.price, base_unit: p.base_unit || p.unit }, 1)} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
            <th className="px-4 py-3">Ürün</th><th className="px-4 py-3 text-right">Birim</th><th className="px-4 py-3 text-center w-36">Miktar</th><th className="px-4 py-3 text-right">Tutar</th><th className="px-4 py-3 w-10"></th>
          </tr></thead>
          <tbody>
            {cart.map((x, i) => (
              <tr key={i} data-testid={`pos-item-${i}`} className="border-b border-stone-100">
                <td className="px-4 py-2.5"><p className="font-medium text-stone-900">{x.name}</p>{x.note && <p className="text-[10px] text-stone-400">{x.note}</p>}</td>
                <td className="px-4 py-2.5 text-right text-stone-600"><input data-testid={`pos-price-${i}`} type="number" min="0" step="0.01" value={x.price} onChange={(e) => setCart((cc) => { const n = [...cc]; n[i] = { ...n[i], price: parseFloat(e.target.value) || 0 }; return n; })} className="w-24 h-8 px-2 rounded-lg border border-stone-300 text-sm text-right focus:border-forest focus:ring-1 focus:ring-forest/30 outline-none" title="Birim fiyat (₺) — düzenlenebilir" /></td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-center gap-1.5">
                    <button data-testid={`pos-qty-minus-${i}`} onClick={() => setQty(i, x.weighed ? -0.05 : -1)} className="p-1 rounded-lg hover:bg-stone-100"><Minus className="w-3.5 h-3.5" /></button>
                    <input data-testid={`pos-qty-${i}`} type="number" min="0" step="0.001" title="Miktar — elle yazabilirsiniz" value={x.quantity}
                      onChange={(e) => setCart((cc) => { const n = [...cc]; n[i] = { ...n[i], quantity: Math.max(0, parseFloat(e.target.value) || 0) }; return n; })}
                      onFocus={(e) => e.target.select()}
                      className="w-20 h-8 px-1 rounded-lg border border-stone-300 text-center font-bold text-sm focus:border-forest focus:ring-1 focus:ring-forest/30 outline-none" />
                    <span data-testid={`pos-unit-${i}`} className="text-xs font-semibold text-stone-500 w-9 shrink-0">{x.base_unit || x.unit || "adet"}</span>
                    <button data-testid={`pos-qty-plus-${i}`} onClick={() => setQty(i, x.weighed ? 0.05 : 1)} className="p-1 rounded-lg hover:bg-stone-100"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right font-bold">{tl(x.price * x.quantity)} ₺</td>
                <td className="px-4 py-2.5 text-right"><button data-testid={`pos-del-${i}`} onClick={() => removeItem(i)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button></td>
              </tr>
            ))}
            {cart.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-stone-400"><Barcode className="w-8 h-8 mx-auto mb-2 opacity-40" />Barkod okutarak satışa başlayın</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-2">
          <button data-testid="pos-pay-nakit" onClick={() => setPayment("nakit")} className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border transition-colors ${payment === "nakit" ? "bg-forest text-white border-forest" : "bg-white text-stone-600 border-stone-300 hover:border-forest"}`}><Banknote className="w-4 h-4" /> Nakit</button>
          <button data-testid="pos-pay-banka" onClick={() => setPayment("banka")} className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border transition-colors ${payment === "banka" ? "bg-forest text-white border-forest" : "bg-white text-stone-600 border-stone-300 hover:border-forest"}`}><CreditCard className="w-4 h-4" /> Kart (POS)</button>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-semibold text-stone-600">{payment === "nakit" ? "Kasa" : "POS Hesabı"}</label>
          <select data-testid="pos-account" className="w-full h-11 px-4 mt-1 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Otomatik (ilk {payment === "nakit" ? "kasa" : "POS"})</option>
            {accounts.filter((a) => payment === "nakit" ? a.account_type === "kasa" : a.account_type !== "kasa").map((a) => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
          </select>
        </div>
        <button data-testid="pos-complete" onClick={complete} disabled={busy || !cart.length} className="bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-base font-bold px-10 py-3.5 rounded-xl transition-colors">
          {busy ? "Kaydediliyor..." : `Satışı Tamamla — ${tl(totals.total)} ₺`}
        </button>
      </div>
    </div>
  );
}
